import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Lock, LogOut, ShieldAlert, X } from "lucide-react";
import LightPayMark from "@/components/LightPayMark";
import PinKeypad from "@/components/PinKeypad";
import AmbientField from "@/components/AmbientField";
import {
  errorCode,
  generateBiometricSecret,
  getBiometricManager,
  retryAfterSeconds,
  setUnlockTicket,
} from "@/lib/appLock";
import { openSupportChat } from "@/lib/support";

const hapticNotify = (type) => {
  try {
    window?.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.(type);
  } catch {
    /* noop */
  }
};

const PIN_LENGTH = 4;
const EMAIL_CODE_LENGTH = 6;

// Кольцо на следующем пустом слоте показывает, куда встанет цифра. Без него
// четыре одинаковых кружка не отвечают на единственный вопрос человека,
// ошибшегося в коде: «сколько я уже набрал».
const Dots = ({ length, filled, shaking, error }) => (
  <div className={`flex items-center justify-center gap-4 ${shaking ? "pin-dots-shake" : ""}`}>
    {Array.from({ length }, (_, i) => {
      const isFilled = i < filled;
      const isNext = !error && i === filled;
      return (
        <span
          key={i}
          className={`h-3.5 w-3.5 rounded-full transition-colors duration-150 ${
            error
              ? "bg-destructive"
              : isFilled
                ? "bg-primary pin-dot-filled"
                : "bg-[var(--lp-tile)]"
          } ${isNext ? "ring-1 ring-primary/45" : ""}`}
        />
      );
    })}
  </div>
);

/**
 * Экран блокировки. Рендерится ВНЕ AppContext.Provider (холодный старт) —
 * всё приходит через props, useApp() здесь недоступен.
 */
const PinLockScreen = ({
  api,
  user,
  telegram,
  variant = "cold",
  reason = "",
  onUnlocked,
  onLogout,
  onDismiss,
}) => {
  const { t } = useTranslation();

  // mode: pin | forgot | forgot-code
  const [mode, setMode] = useState("pin");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [lockSeconds, setLockSeconds] = useState(0);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const bioTriedRef = useRef(false);
  const mountedRef = useRef(true);

  const biometricEnabled = Boolean(user?.settings?.biometric_enabled);
  const biometricType = String(window?.Telegram?.WebApp?.BiometricManager?.biometricType || "unknown");
  const linkedEmail = String(user?.email || "").trim();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Escape закрывает только шаг-ап: у замка выхода нет по замыслу.
  useEffect(() => {
    if (variant !== "sheet" || !onDismiss) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant, onDismiss]);

  // Обратный отсчёт локаута — строго от retry_after_seconds сервера.
  useEffect(() => {
    if (lockSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setLockSeconds((s) => (s > 1 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [lockSeconds > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const shake = () => {
    setShaking(true);
    setTimeout(() => mountedRef.current && setShaking(false), 450);
  };

  const finishUnlock = useCallback(
    (ticket, extra = {}) => {
      if (ticket) setUnlockTicket(ticket);
      hapticNotify("success");
      onUnlocked?.(ticket, extra);
    },
    [onUnlocked]
  );

  const submitPin = useCallback(
    async (pin) => {
      setBusy(true);
      setErrorText("");
      try {
        const res = await api.post("/security/pin/verify", { pin });
        finishUnlock(res.data?.unlock_ticket || null);
      } catch (err) {
        if (!mountedRef.current) return;
        setValue("");
        const code = errorCode(err);
        const retry = retryAfterSeconds(err);
        if (code === "pin_locked" || (code === "invalid_pin" && retry > 0)) {
          setLockSeconds(retry);
          if (code === "invalid_pin") {
            hapticNotify("error");
            shake();
          }
        } else if (code === "invalid_pin") {
          hapticNotify("error");
          shake();
          const failsLeft = Number(err?.response?.data?.detail?.fails_left);
          setErrorText(
            Number.isFinite(failsLeft) && failsLeft > 0
              ? t("security.wrong_pin_left", { count: failsLeft })
              : t("security.wrong_pin")
          );
        } else if (code === "pin_not_set") {
          // Пин сняли с другого устройства — просто пропускаем.
          finishUnlock(null, { pinDisabled: true });
        } else if (!err?.response) {
          setErrorText(t("security.network_error"));
        } else {
          setErrorText(t("security.generic_error"));
        }
      } finally {
        if (mountedRef.current) setBusy(false);
      }
    },
    [api, finishUnlock, t]
  );

  const submitEmailCode = useCallback(
    async (code) => {
      setBusy(true);
      setErrorText("");
      try {
        await api.post("/auth/email/code/verify", {
          email: linkedEmail,
          code,
          purpose: "pin_reset",
        });
        // Пин сброшен — впускаем без тикета (пин теперь выключен).
        finishUnlock(null, { pinDisabled: true });
      } catch (err) {
        if (!mountedRef.current) return;
        setValue("");
        hapticNotify("error");
        shake();
        const status = err?.response?.status;
        if (status === 401) setErrorText(t("security.reset_code_wrong"));
        else if (status === 429) setErrorText(t("security.reset_code_too_many"));
        else if (status === 400) setErrorText(t("security.reset_code_expired"));
        else if (!err?.response) setErrorText(t("security.network_error"));
        else setErrorText(t("security.generic_error"));
      } finally {
        if (mountedRef.current) setBusy(false);
      }
    },
    [api, linkedEmail, finishUnlock, t]
  );

  const onDigit = (digit) => {
    if (busy || lockSeconds > 0) return;
    const max = mode === "forgot-code" ? EMAIL_CODE_LENGTH : PIN_LENGTH;
    const next = (value + digit).slice(0, max);
    setValue(next);
    setErrorText("");
    if (next.length === max) {
      if (mode === "forgot-code") submitEmailCode(next);
      else submitPin(next);
    }
  };

  const onBackspace = () => {
    if (busy) return;
    setValue((v) => v.slice(0, -1));
  };

  const tryBiometric = useCallback(async () => {
    const { bm, available } = await getBiometricManager();
    if (!bm || !available) return false;
    return new Promise((resolve) => {
      try {
        bm.authenticate({ reason: t("security.bio_reason") }, async (ok, token) => {
          if (!ok || !token) {
            resolve(false);
            return;
          }
          try {
            const res = await api.post("/security/unlock/biometric", { secret: token });
            finishUnlock(res.data?.unlock_ticket || null);
            resolve(true);
          } catch (err) {
            if (errorCode(err) === "biometric_stale") {
              // Пин меняли в другом месте — локальная привязка мертва, чистим.
              try {
                bm.updateBiometricToken("", () => {});
              } catch {
                /* noop */
              }
              if (mountedRef.current) setErrorText(t("security.bio_stale"));
            }
            resolve(false);
          }
        });
      } catch {
        resolve(false);
      }
    });
  }, [api, finishUnlock, t]);

  // Авто-попытка биометрии: один раз, только на видимом экране, без циклов.
  useEffect(() => {
    if (bioTriedRef.current || !biometricEnabled) return;
    if (document.visibilityState !== "visible") return;
    bioTriedRef.current = true;
    (async () => {
      const { available } = await getBiometricManager();
      if (!mountedRef.current) return;
      setBiometricAvailable(available);
      if (available) await tryBiometric();
    })();
  }, [biometricEnabled, tryBiometric]);

  const startForgotFlow = async () => {
    if (!linkedEmail) {
      setMode("forgot");
      return;
    }
    setForgotBusy(true);
    setErrorText("");
    try {
      await api.post("/auth/email/code/request", { email: linkedEmail, purpose: "pin_reset" });
      setValue("");
      setMode("forgot-code");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        const detail = String(err?.response?.data?.detail || "");
        setErrorText(detail || t("security.reset_code_too_many"));
      } else if (status === 500) {
        setErrorText(t("security.reset_unavailable"));
      } else if (!err?.response) {
        setErrorText(t("security.network_error"));
      } else {
        setErrorText(t("security.generic_error"));
      }
    } finally {
      setForgotBusy(false);
    }
  };

  const isCodeEntry = mode === "forgot-code";
  const locked = lockSeconds > 0;

  // ── Шаг-ап: лист вместо экрана ──────────────────────────────────────────
  //
  // Разница с замком не в оформлении, а в смысле. Замок говорит «кошелёк
  // закрыт» — он непрозрачен, несёт логотип и предлагает выйти из аккаунта.
  // Здесь кошелёк открыт, и спрашивают согласие на одно действие: карта
  // остаётся за размытием, выйти нельзя, отказаться — можно.
  //
  // Логика ввода общая с замком намеренно: локаут, счётчик попыток,
  // биометрия и восстановление по почте не должны разойтись в двух копиях
  // просто потому, что у окна другая рамка.
  if (variant === "sheet") {
    const ReasonIcon = reason === "card_secure" ? CreditCard : Lock;
    const subtitle =
      reason === "card_secure"
        ? t("security.stepup_card_subtitle")
        : t("security.stepup_generic_subtitle");

    return (
      <div
        className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
        data-testid="pin-step-up"
      >
        <button
          type="button"
          className="lp-scrim--fixed"
          onClick={onDismiss}
          aria-label={t("common.close")}
          tabIndex={-1}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="pin-step-up-title"
          className="lp-sheet relative max-h-[94vh] w-full max-w-md overflow-y-auto overscroll-contain px-5 pb-[calc(1.25rem+var(--lp-bottom))] pt-6 sm:rounded-[24px]"
        >
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t("common.close")}
            className="lp-press absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--lp-tile)] text-muted-foreground"
            data-testid="pin-step-up-close"
          >
            <X className="h-4 w-4" />
          </button>

          {mode === "forgot" ? (
            <div className="flex flex-col items-center gap-3 px-2 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <ShieldAlert className="h-7 w-7 text-link" aria-hidden="true" />
              </div>
              <h2 id="pin-step-up-title" className="text-lg font-bold text-foreground">
                {t("security.reset_title")}
              </h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {t("security.reset_no_email")}
              </p>
              <button
                type="button"
                className="lp-press mt-2 flex min-h-[48px] w-full items-center justify-center rounded-[14px] bg-primary px-4 text-sm font-semibold text-primary-foreground"
                onClick={() => openSupportChat(telegram)}
              >
                {t("security.contact_support")}
              </button>
              <button
                type="button"
                className="lp-press flex min-h-[48px] w-full items-center justify-center rounded-[14px] text-sm font-medium text-muted-foreground"
                onClick={() => setMode("pin")}
              >
                {t("common.back")}
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <ReasonIcon className="h-7 w-7 text-link" aria-hidden="true" />
              </div>

              <div className="px-6 text-center">
                <h2 id="pin-step-up-title" className="text-lg font-bold text-foreground">
                  {isCodeEntry ? t("security.reset_code_title") : t("security.enter_pin")}
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {isCodeEntry
                    ? t("security.reset_code_subtitle", { email: linkedEmail })
                    : subtitle}
                </p>
              </div>

              <Dots
                length={isCodeEntry ? EMAIL_CODE_LENGTH : PIN_LENGTH}
                filled={value.length}
                shaking={shaking}
                error={Boolean(errorText) && !locked}
              />

              {/* Высота зафиксирована: без неё появление ошибки подбрасывало бы
                  клавиатуру под уже занесённым пальцем. */}
              <div className="flex h-5 items-center" aria-live="polite">
                {locked ? (
                  <p className="text-sm font-medium text-destructive-fg">
                    {t("security.locked_for", { seconds: lockSeconds })}
                  </p>
                ) : errorText ? (
                  <p className="text-sm text-destructive-fg">{errorText}</p>
                ) : busy ? (
                  <div className="spinner" />
                ) : null}
              </div>

              <PinKeypad
                onDigit={onDigit}
                onBackspace={onBackspace}
                onBiometric={
                  !isCodeEntry && biometricEnabled && biometricAvailable
                    ? () => tryBiometric()
                    : undefined
                }
                biometricType={biometricType === "face" ? "face" : "finger"}
                disabled={busy || locked}
              />

              {isCodeEntry ? (
                <button
                  type="button"
                  className="lp-press flex min-h-[44px] items-center px-4 text-sm text-muted-foreground"
                  onClick={() => {
                    setMode("pin");
                    setValue("");
                    setErrorText("");
                  }}
                >
                  {t("common.back")}
                </button>
              ) : (
                <button
                  type="button"
                  className="lp-press flex min-h-[44px] items-center px-4 text-sm font-medium text-link disabled:opacity-50"
                  onClick={startForgotFlow}
                  disabled={forgotBusy}
                  data-testid="pin-step-up-forgot"
                >
                  {t("security.forgot_pin")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Экран непрозрачен намеренно: за замком не должно просвечивать ничего из
  // кошелька. Поэтому сцену он несёт свою — см. AmbientField.
  const containerClass =
    variant === "overlay"
      ? "fixed inset-0 z-[100] bg-background flex flex-col"
      : "app-container bg-background flex flex-col";

  // Экран «почта не привязана».
  if (mode === "forgot") {
    return (
      <div className={containerClass} data-testid="pin-lock-screen">
        <AmbientField />
        <div className="lp-layer flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <LightPayMark className="h-14 w-14" />
          <h1 className="text-lg font-bold text-foreground">{t("security.reset_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("security.reset_no_email")}</p>
          <div className="mt-2 flex flex-col items-center gap-3">
            <button
              type="button"
              className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
              onClick={() => openSupportChat(telegram)}
            >
              {t("security.contact_support")}
            </button>
            <button
              type="button"
              className="text-sm text-muted-foreground"
              onClick={() => setMode("pin")}
            >
              {t("common.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass} data-testid="pin-lock-screen">
      <AmbientField />
      <div className="lp-layer flex flex-1 flex-col items-center justify-center gap-6 px-6">
        <div className="flex flex-col items-center gap-3">
          <LightPayMark className="h-14 w-14" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground">
              {isCodeEntry ? t("security.reset_code_title") : t("security.enter_pin")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {isCodeEntry
                ? t("security.reset_code_subtitle", { email: linkedEmail })
                : t("security.enter_pin_subtitle")}
            </p>
          </div>
        </div>

        <Dots
          length={isCodeEntry ? EMAIL_CODE_LENGTH : PIN_LENGTH}
          filled={value.length}
          shaking={shaking}
          error={Boolean(errorText) && !locked}
        />

        <div className="flex h-5 items-center">
          {locked ? (
            <p className="text-sm font-medium text-destructive-fg">
              {t("security.locked_for", { seconds: lockSeconds })}
            </p>
          ) : errorText ? (
            <p className="text-sm text-destructive-fg">{errorText}</p>
          ) : busy ? (
            <div className="spinner" />
          ) : null}
        </div>

        <PinKeypad
          onDigit={onDigit}
          onBackspace={onBackspace}
          onBiometric={
            !isCodeEntry && biometricEnabled && biometricAvailable ? () => tryBiometric() : undefined
          }
          biometricType={biometricType === "face" ? "face" : "finger"}
          disabled={busy || locked}
        />
      </div>

      <div className="flex flex-col items-center gap-3 pb-[calc(1.5rem+var(--lp-bottom))]">
        {isCodeEntry ? (
          <button
            type="button"
            className="text-sm text-muted-foreground"
            onClick={() => {
              setMode("pin");
              setValue("");
              setErrorText("");
            }}
          >
            {t("common.back")}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="text-sm font-medium text-link disabled:opacity-50"
              onClick={startForgotFlow}
              disabled={forgotBusy}
              data-testid="pin-forgot-btn"
            >
              {t("security.forgot_pin")}
            </button>
            {onLogout && (
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm text-muted-foreground"
                onClick={onLogout}
              >
                <LogOut className="h-4 w-4" />
                {t("profile.logout")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PinLockScreen;
